import React, { useState } from 'react';
import { Card, Stack, LoadingOverlay, Container, Badge } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';
import { masterUserAdminService } from 'services/dacum/masterUserAdminService';
import { MasterUserAdmin } from 'services/api';
import PageTitle from 'components/PageTitle';
import Toolbar from 'components/Toolbar';
import DataTables from 'components/DataTables';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import Dates from 'components/Dates';

const MasterUserAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
  };

  const handleChangeSize = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const defaultSearch = new URLSearchParams(location.search).get('search') || '';

  const [opened, { open, close }] = useDisclosure();
  const [userAdminToDelete, setUserAdminToDelete] = useState<string | null>(null);

  const {
    data: userAdminsData,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: ['masterUserAdmins', defaultSearch, currentPage, pageSize],
    queryFn: () =>
      masterUserAdminService.getList({
        search: defaultSearch,
        page: currentPage,
        limit: pageSize
      }),
    refetchOnWindowFocus: false
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => masterUserAdminService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['masterUserAdmins'] });
      close();
      showSuccessNotification({
        title: 'Deleted Successfully!',
        message: 'Admin user has been deleted successfully'
      });
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Failed to delete admin user',
        message: error?.message || 'Something went wrong please try again'
      });
    }
  });

  const columns = [
    {
      heading: 'Binusian ID',
      props: 'adminID',
      sortable: true,
      active: true,
      id: 'adminID'
    },
    {
      heading: 'Admin Name',
      props: 'adminName',
      sortable: true,
      active: true,
      id: 'adminName'
    },
    {
      heading: 'Areas',
      props: (row: MasterUserAdmin) => {
        if (row.listArea && Array.isArray(row.listArea)) {
          return (
            <div>
              {row.listArea.map((area, index) => (
                <Badge
                  key={index}
                  variant='light'
                  size='sm'
                  style={{ marginRight: 4, marginBottom: 2 }}
                >
                  {area.areaName || area.areaId}
                </Badge>
              ))}
            </div>
          );
        } else if (row.areaName) {
          return (
            <Badge variant='light' size='sm'>
              {row.areaName}
            </Badge>
          );
        }
        return (
          <Badge variant='light' size='sm'>
            No Area Assigned
          </Badge>
        );
      },
      sortable: false,
      active: true
    },
    {
      heading: 'Email',
      props: 'adminEmail',
      sortable: true,
      active: true,
      id: 'adminEmail'
    },
    {
      heading: 'Beelajar Email',
      props: (row: MasterUserAdmin) => row.beelajarEmail || '-',
      sortable: false,
      active: true,
      id: 'beelajarEmail'
    },

    {
      heading: 'Created Date',
      props: (row: MasterUserAdmin) => <Dates date={row.dateIn} dateFormat='dateTime' />,
      sortable: true,
      active: true,
      id: 'dateIn'
    },
    {
      heading: 'Action',
      props: 'action',
      active: true,
      id: 'action'
    }
  ];

  const handleView = (row: MasterUserAdmin) => {
    navigate(`/app/dacum/master-user-admin/${row.msUserAdminID}`);
  };

  const handleEdit = (row: MasterUserAdmin) => {
    navigate(`/app/dacum/master-user-admin/edit/${row.msUserAdminID}`);
  };

  const handleDelete = (row: MasterUserAdmin) => {
    setUserAdminToDelete(row.msUserAdminID);
    open();
  };

  const handleCreateClick = () => {
    navigate('/app/dacum/master-user-admin/add');
  };

  const processDelete = async () => {
    if (userAdminToDelete) {
      await deleteMutation.mutateAsync(userAdminToDelete);
    }
  };

  return (
    <Container fluid>
      <ModalPopupConfirmationComponent
        buttonText='Delete'
        statusProcess='delete'
        opened={opened}
        open={open}
        close={close}
        onSubmit={processDelete}
      />

      <PageTitle
        title='Master Admin User'
        description='Create and manage admin users. Assign users to specific areas for administration and management purposes.'
      />

      <Card shadow='xs' radius='md' mt={20} pos='relative'>
        <LoadingOverlay visible={isLoading || isFetching || deleteMutation.isPending} />

        <Toolbar
          showSearch
          showCreateButton
          searchPlaceholder='Search admin users...'
          createButtonText='Add New Admin User'
          defaultSearchValue={defaultSearch}
          onCreate={handleCreateClick}
          showFilter={false}
        />

        <Stack mt={20}>
          <DataTables
            columns={columns}
            data={userAdminsData?.data || []}
            loading={isLoading}
            isNumbering
            activeSize={pageSize}
            currentSize={pageSize}
            currentPage={currentPage}
            totalData={userAdminsData?.totalRecords}
            withPagination
            handleView={handleView}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleChangePage={handleChangePage}
            handleChangeSize={handleChangeSize}
            emptyMessageTitle='No admin users found'
            emptyMessageDescription='Create your first admin user by clicking the Add New Admin User button'
          />
        </Stack>
      </Card>
    </Container>
  );
};

export default MasterUserAdminPage;
